import { scheduleGridToValues, scheduleValuesToGrid } from "features/transient-response/schedules/lib/scheduleGrid";
import { createEmptyGrid } from "features/transient-response/schedules/lib/scheduleHeatmap";
import { DAY_KEYS, type DayKey, HOUR_KEYS, type ScheduleGrid } from "features/transient-response/schedules/model/types";

import type {
  RoomScheduleApiItem,
  RoomScheduleApiType,
  RoomScheduleHeatmapKey,
  RoomScheduleHeatmapState,
  RoomScheduleMatrix,
  RoomScheduleMode,
  RoomScheduleRealisticCatalog,
  RoomSchedulesApi,
  RoomSchedulesState,
} from "../model/scheduleTypes";

import { ROOM_SCHEDULE_HEATMAP_KEYS } from "../model/scheduleTypes";
import {
  cloneScheduleGrid,
  createEmptyScheduleGrid,
  createZeroScheduleGrid,
  isScheduleGridPopulated,
} from "./roomSchedulePresets";

const API_DAY_COLUMNS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun", "holiday"] as const;

const DAY_LABEL_TO_KEY: Record<string, DayKey> = {
  fri: "fri",
  friday: "fri",
  holiday: "holiday",
  mon: "mon",
  monday: "mon",
  sat: "sat",
  saturday: "sat",
  sun: "sun",
  sunday: "sun",
  thu: "thu",
  thursday: "thu",
  tue: "tue",
  tuesday: "tue",
  wed: "wed",
  wednesday: "wed",
};

export const toApiScheduleType = (mode: RoomScheduleMode): RoomScheduleApiType => {
  if (mode === "custom") return "Customized";
  if (mode === "din") return "DIN";
  return "realistic";
};

export const fromApiScheduleType = (type: unknown): RoomScheduleMode => {
  const normalized = typeof type === "string" ? type.trim().toLowerCase() : "";
  if (normalized === "customized" || normalized === "custom") return "custom";
  if (normalized === "din") return "din";
  return "realistic";
};

const normalizeDayLabel = (label: string): DayKey | undefined => DAY_LABEL_TO_KEY[label.trim().toLowerCase()];

export const scheduleGridToMatrix = (grid: ScheduleGrid): RoomScheduleMatrix => ({
  columns: [...API_DAY_COLUMNS],
  rows: [...HOUR_KEYS],
  values: scheduleGridToValues(grid),
});

export const scheduleMatrixToGrid = (data?: null | RoomScheduleMatrix): ScheduleGrid => {
  if (!data?.values?.length) return createEmptyScheduleGrid();

  const columns = (data.columns?.length ? data.columns : API_DAY_COLUMNS).map((column) => String(column));
  const dayKeys = columns.map((column) => normalizeDayLabel(column)).filter((day): day is DayKey => Boolean(day));

  if (dayKeys.length === DAY_KEYS.length && dayKeys.every((day, index) => day === DAY_KEYS[index])) {
    return scheduleValuesToGrid(data.values);
  }

  const grid = createEmptyGrid();
  data.values.forEach((row, rowIndex) => {
    const hour = data.rows?.[rowIndex] ?? rowIndex;
    if (typeof hour !== "number" || hour < 0 || hour > 23) return;
    row?.forEach((value, columnIndex) => {
      const day = dayKeys[columnIndex];
      if (!day) return;
      grid[day][hour] = value === undefined ? null : value;
    });
  });
  return grid;
};

const isScheduleMatrix = (value: unknown): value is RoomScheduleMatrix => {
  if (!value || typeof value !== "object") return false;
  return Array.isArray((value as RoomScheduleMatrix).values);
};

export const parseRoomScheduleApiItem = (value: unknown): null | RoomScheduleApiItem => {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  if (isScheduleMatrix(row.data)) {
    return { data: row.data, type: toApiScheduleType(fromApiScheduleType(row.type)) };
  }
  if (isScheduleMatrix(row)) {
    return { data: row, type: "realistic" };
  }
  if (typeof row.type === "string") {
    return { data: null, type: toApiScheduleType(fromApiScheduleType(row.type)) };
  }
  return null;
};

export const parseRealisticCatalogResponse = (value: unknown): Partial<Record<string, RoomScheduleApiItem>> => {
  if (!value || typeof value !== "object") return {};
  const roomTypes = (value as { roomTypes?: unknown }).roomTypes;
  if (!roomTypes || typeof roomTypes !== "object") return {};

  return Object.fromEntries(
    Object.entries(roomTypes).flatMap(([roomType, raw]) => {
      const item = parseRoomScheduleApiItem(raw);
      return item ? [[roomType, item]] : [];
    })
  );
};

export const lookupRealisticSchedule = (
  catalog: RoomScheduleRealisticCatalog,
  kind: RoomScheduleHeatmapKey,
  roomNames: readonly (string | undefined)[]
): null | RoomScheduleApiItem => {
  const roomTypes = catalog[kind];
  if (!roomTypes) return null;

  for (const name of roomNames) {
    const trimmed = name?.trim();
    if (!trimmed) continue;
    const match =
      roomTypes[trimmed] ?? Object.entries(roomTypes).find(([key]) => key.toLowerCase() === trimmed.toLowerCase())?.[1];
    const item = parseRoomScheduleApiItem(match);
    if (item) return item;
  }
  return null;
};

export const resolveCustomScheduleGrid = (grid: ScheduleGrid): ScheduleGrid =>
  isScheduleGridPopulated(grid) ? grid : createZeroScheduleGrid();

export const defaultRoomScheduleHeatmapState = (): RoomScheduleHeatmapState => ({
  customGrid: createZeroScheduleGrid(),
  mode: "custom",
});

export const defaultRoomSchedulesState = (): RoomSchedulesState =>
  Object.fromEntries(
    ROOM_SCHEDULE_HEATMAP_KEYS.map((key) => [key, defaultRoomScheduleHeatmapState()])
  ) as RoomSchedulesState;

export const mapApiSchedulesToState = (schedules?: null | RoomSchedulesApi): RoomSchedulesState => {
  const state = defaultRoomSchedulesState();
  ROOM_SCHEDULE_HEATMAP_KEYS.forEach((key) => {
    const item = parseRoomScheduleApiItem(schedules?.[key]);
    if (!item) return;
    const mode = fromApiScheduleType(item.type);
    const customGrid =
      mode === "custom" ? resolveCustomScheduleGrid(scheduleMatrixToGrid(item.data)) : createEmptyScheduleGrid();
    state[key] = { customGrid, mode };
  });
  return state;
};

export const buildRoomSchedulesPayload = (
  state: RoomSchedulesState,
  resolveGrid: (key: RoomScheduleHeatmapKey) => ScheduleGrid
): RoomSchedulesApi =>
  Object.fromEntries(
    ROOM_SCHEDULE_HEATMAP_KEYS.map((key) => {
      const grid = resolveGrid(key);
      return [
        key,
        {
          data: isScheduleGridPopulated(grid) ? scheduleGridToMatrix(grid) : null,
          type: toApiScheduleType(state[key].mode),
        },
      ];
    })
  ) as RoomSchedulesApi;

export const copyGridToCustom = (grid: ScheduleGrid): RoomScheduleHeatmapState => ({
  customGrid: cloneScheduleGrid(grid),
  mode: "custom",
});
